import { $Q } from '../utils/querySelector';
import { debounce } from 'lodash';

/**
 * @typedef {object} AutocompleteItem
 * @property {string} label - Text to display on list items
 * @property {object} data - extra data to send to event when the
 */

/**
 * @typedef {object} Events
 * @property {(value: string) => Promise<Array<AutocompleteItem>>} onType
 *  - function to execute when the user types.
 * @property {(item: AutocompleteItem) => void}  onClick
 *  - function to execute when user clicks on an option
 * @property {(item: AutocompleteItem, element: HTMLLIElement) => void} beforeItemRender
 *  - function to execute when each item is being created as an html element before render
 */

/**
 * @typedef {object} AutocompleteOptions
 * @property {AutocompleteItem[]} defaultItems
 * @property {boolean} useDefaultItems
 *  - Indicates if the type autocomplete will use the default items to provide the items' list. If set to false, the onType event should be provided
 * @property {Events} events
 *  - Custom user defined events to execute
 * @property {number} debounce
 *  - if set to a value greather than 0, the onType function will be debounced
 */

/**
 *
 * Registers an autocomplete input with the given options. Right now it is very tailored to the specific needs of
 * the product-registration use case, though it has potential to tackle more simple use cases
 *
 * @param {string} inputSelector - input selector
 * @param {AutocompleteOptions} options - options to define interactions with the autocomplete items
 */
export const autocomplete = (inputSelector, options) => {
  // initialize html elements
  const inputElement = $Q(inputSelector);
  const wrapperElement = document.createElement('div');
  const focusEffect = document.createElement('span');
  wrapperElement.classList.add('autocomplete');
  focusEffect.classList.add('focus-bg');

  const autoCompleteContainer = document.createElement('ul');
  const fakeItem = document.createElement('li');
  fakeItem.classList.add('autocomplete__item');

  inputElement.parentNode.insertBefore(wrapperElement, inputElement);
  wrapperElement.appendChild(inputElement);
  wrapperElement.appendChild(autoCompleteContainer);
  wrapperElement.appendChild(focusEffect);

  // if debounce is enabled, then create a debounced function to use in

  let triggerFunc = () => {};
  if (options.debounce) {
    triggerFunc = debounce(
      (query) =>
        options.events.onType(query).then((items) => renderItems(items)),
      options.debounce,
      {
        trailing: true,
      }
    );
  } else {
    triggerFunc = (query) =>
      options.events.onType(query).then((items) => renderItems(items));
  }

  // initialize events
  inputElement.addEventListener('input', (e) => {
    triggerFunc(e.target.value);
  });

  // manage items

  /**
   *
   * @param {Array<AutocompleteItem>} items
   */
  const renderItems = (items) => {
    autoCompleteContainer.innerHTML = '';

    if (items.length === 0) {
      console.log('Items are now 0');
      inputElement.removeAttribute('data-extra');
      autoCompleteContainer.classList.remove('shown');
      return;
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const htmlItem = fakeItem.cloneNode();
      htmlItem.appendChild(document.createTextNode(item.label));
      htmlItem.setAttribute('data-extra', JSON.stringify(item.data));

      htmlItem.addEventListener('click', (e) => {
        inputElement.value = item.label;
        inputElement.setAttribute('data-extra', e.target.dataset.extra);
        autoCompleteContainer.classList.remove('shown');
      });

      fragment.appendChild(htmlItem);
    });

    autoCompleteContainer.appendChild(fragment);

    autoCompleteContainer.classList.add('shown');
  };
};
